import { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  ArrowDown,
  ArrowRight,
  Download,
  Plus,
  Minus,
  Menu,
  X,
  Check,
  Copy,
} from "lucide-react";
import { profile, projects, experience, toolkit, archive } from "./data";
import MathStudy from "./components/MathStudy";
import ProjectArtwork from "./components/ProjectArtwork";
import ProjectDialog from "./components/ProjectDialog";
import "./app.scss";

const navigation = [
  { href: "#work", label: "Work" },
  { href: "#story", label: "My story" },
  { href: "#notebook", label: "Notebook" },
];
const filters = [
  { id: "all", name: "All work" },
  { id: "ai", name: "Applied AI" },
  { id: "systems", name: "Systems" },
  { id: "math", name: "Math & motion" },
];
function SectionLabel({ number, children }) {
  return (
    <div className="section-label">
      <span className="section-index">{number}</span>
      <span>{children}</span>
    </div>
  );
}
function Header() {
  const [open, setOpen] = useState(false);
  const firstLinkRef = useRef(null);
  const toggleRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    firstLinkRef.current?.focus();
    const close = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  return (
    <header className="site-header">
      <div className="header-inner">
        <a
          className="identity"
          href="#home"
          aria-label="Priyajan Swongamikha, home"
          onClick={() => setOpen(false)}
        >
          <span className="identity-mark" aria-hidden="true">
            p<span>.</span>
          </span>
          <span>
            Priyajan
            <br />
            Swongamikha<span className="name-dot">.</span>
          </span>
        </a>
        <span className="header-note">
          A work in progress.
          <br />
          <span>Just like its maker.</span>
        </span>
        <nav
          className={`main-nav ${open ? "is-open" : ""}`}
          aria-label="Main navigation"
          id="main-navigation"
        >
          {navigation.map((item, index) => (
            <a
              key={item.href}
              href={item.href}
              ref={index === 0 ? firstLinkRef : null}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </a>
          ))}
          <a
            className="nav-contact"
            href="#contact"
            onClick={() => setOpen(false)}
          >
            Let’s talk <ArrowUpRight size={15} />
          </a>
        </nav>
        <button
          ref={toggleRef}
          className="mobile-menu"
          aria-label={open ? "Close navigation" : "Open navigation"}
          aria-expanded={open}
          aria-controls="main-navigation"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}
function Work({ onSelect }) {
  const [filter, setFilter] = useState("all");
  const visible = projects.filter(
    (project) => filter === "all" || project.category === filter,
  );
  return (
    <section
      className="work-section section-space"
      id="work"
      aria-labelledby="work-heading"
    >
      <SectionLabel number="01">SELECTED WORK</SectionLabel>
      <div className="section-heading-row">
        <h2 id="work-heading">
          Ideas, out in
          <br />
          <em>the world.</em>
        </h2>
        <p>
          From a model in a notebook to a system someone can use. A few things
          I’ve built, and what they’ve taught me.
        </p>
      </div>
      <div className="work-toolbar">
        <div className="work-filters" aria-label="Filter selected work">
          {filters.map((item) => (
            <button
              key={item.id}
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.name}
              {filter === item.id && <span aria-hidden="true"> ↗</span>}
            </button>
          ))}
        </div>
        <span className="mono work-count" aria-live="polite">
          {String(visible.length).padStart(2, "0")} ENTRIES
        </span>
      </div>
      <div className="projects-grid">
        {visible.map((project) => (
          <article className="project" key={project.id}>
            <button
              className={`project-cover cover-${project.cover}`}
              onClick={(event) => onSelect(project, event.currentTarget)}
              aria-label={`Read the story of ${project.title}`}
            >
              <ProjectArtwork kind={project.cover} />
              <span className="cover-note mono">{project.note}</span>
              <span className="cover-open" aria-hidden="true">
                <ArrowUpRight size={22} />
              </span>
            </button>
            <div className="project-meta mono">
              <span>{project.discipline}</span>
              <span>/{project.number}</span>
            </div>
            <button
              className="project-title"
              onClick={(event) => onSelect(project, event.currentTarget)}
            >
              <h3>{project.title}</h3>
              <ArrowUpRight size={23} />
            </button>
            <p className="project-description">{project.description}</p>
            <div className="project-tech">
              {project.tech.map((tech) => (
                <span key={tech}>{tech}</span>
              ))}
            </div>
          </article>
        ))}
      </div>
      <div className="work-footnote">
        <span className="mono">A SELECTION, NOT THE WHOLE STORY.</span>
        <a href={profile.github} target="_blank" rel="noreferrer">
          More on GitHub <ArrowUpRight size={16} />
        </a>
      </div>
    </section>
  );
}
function Story() {
  return (
    <section
      className="story-section section-space"
      id="story"
      aria-labelledby="story-heading"
    >
      <div className="story-top">
        <div className="story-copy">
          <SectionLabel number="02">
            THE THREAD THAT CONNECTS IT ALL
          </SectionLabel>
          <h2 id="story-heading">
            It started with
            <br />a love of <em>maths.</em>
          </h2>
          <p className="story-lead">
            I learned to code so I could speak with computers. I kept building
            because I wanted to see what was possible.
          </p>
          <p>
            Mathematical animations led to machine learning. Machine learning
            led to a question: how do I put this in someone’s hands? That
            question took me into full-stack development.
          </p>
          <p>
            Today, I work across AI and software engineering. The common thread
            is still the same: understand something deeply enough to build with
            it, then make it useful enough to share.
          </p>
          <div className="story-signoff">
            <span>Always a student.</span>
            <span className="signature">Priyajan.</span>
          </div>
        </div>
        <div className="story-image">
          <div className="portrait-background">
            <img
              src="/assets/priyajan-portrait-edited.webp"
              alt="Priyajan in a beige jacket, looking toward the Himalayan mountains"
              width="1122"
              height="1402"
              loading="lazy"
            />
          </div>
          <span className="image-caption mono">
            A LITTLE FURTHER FROM THE KEYBOARD.
          </span>
          <span className="portrait-stamp" aria-hidden="true">
            Still
            <br />
            <em>figuring it out.</em>
            <span>↗</span>
          </span>
        </div>
      </div>
      <div className="journey-heading">
        <h3>A little further, every time.</h3>
        <span className="mono">EXPERIENCE / 2022—2026</span>
      </div>
      <div className="experience-list">
        {experience.map((item) => (
          <article className="experience-row" key={item.company}>
            <span className="experience-period mono">
              {item.current && <span className="status-dot" />}
              {item.period}
            </span>
            <div className="experience-identity">
              <h4>{item.company}</h4>
              <span>{item.role}</span>
            </div>
            <div className="experience-description">
              <p>{item.description}</p>
              <span className="experience-stack">{item.detail}</span>
              <p className="experience-learning">↳ {item.learning}</p>
            </div>
          </article>
        ))}
      </div>
      <div className="education-note">
        <span className="mono">ALONG THE WAY</span>
        <p>
          Bachelor’s in Computer Engineering, Kathford <span>2023—present</span>
          <br />
          Science at St. Xavier’s College <span>2019—2021</span>
        </p>
        <a className="text-link" href={profile.cv} download>
          Read my CV <Download size={16} />
        </a>
      </div>
    </section>
  );
}
function Notebook() {
  const [expanded, setExpanded] = useState(null);
  return (
    <section
      className="notebook-section section-space"
      id="notebook"
      aria-labelledby="notebook-heading"
    >
      <SectionLabel number="03">THE NOTEBOOK</SectionLabel>
      <div className="section-heading-row">
        <h2 id="notebook-heading">
          Stay curious.
          <br />
          <em>Make things.</em>
        </h2>
        <p>
          The side paths are part of the journey. Small experiments, first
          principles, and things made for the joy of understanding.
        </p>
      </div>
      <div className="notebook-grid">
        <article className="math-notebook">
          <div className="notebook-note mono">
            AFTERIMAGE / VISUAL MATHEMATICS <span>01</span>
          </div>
          <svg
            className="sine-study"
            viewBox="0 0 600 235"
            role="img"
            aria-label="Sine and cosine curves showing the relationship between waves"
          >
            <defs>
              <pattern
                id="graph-grid"
                width="25"
                height="25"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 25 0 L 0 0 0 25"
                  fill="none"
                  stroke="currentColor"
                  strokeOpacity=".09"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="600" height="235" fill="url(#graph-grid)" />
            <path
              d="M30 120H570M65 25V215"
              fill="none"
              stroke="currentColor"
              strokeOpacity=".25"
            />
            <path
              d={Array.from(
                { length: 220 },
                (_, i) =>
                  `${i ? "L" : "M"}${35 + i * 2.4},${120 - Math.sin(i / 27) * 70}`,
              ).join(" ")}
              fill="none"
              stroke="#b7472a"
              strokeWidth="2.5"
            />
            <path
              d={Array.from(
                { length: 220 },
                (_, i) =>
                  `${i ? "L" : "M"}${35 + i * 2.4},${120 - Math.cos(i / 27) * 70}`,
              ).join(" ")}
              fill="none"
              stroke="#6f7864"
              strokeWidth="1.8"
              strokeDasharray="5 5"
            />
            <text x="460" y="34" className="graph-label">
              f(x) = sin(x)
            </text>
            <circle cx="137" cy="50" r="4" fill="#b7472a" />
          </svg>
          <h3>Afterimage</h3>
          <p>
            I always wanted to make maths videos. Afterimage is my test channel
            for a text-to-video pipeline: a way to make the scripting,
            animation, and editing less of a barrier to sharing the ideas.
          </p>
          {profile.afterimage ? (
            <a
              className="text-link"
              href={profile.afterimage}
              target="_blank"
              rel="noreferrer"
            >
              Explore Afterimage <ArrowUpRight size={16} />
            </a>
          ) : (
            <span className="channel-note">
              For the beauty of understanding.
            </span>
          )}
        </article>
        <article className="devlog-notebook">
          <div className="notebook-note mono">
            LASTSTANDAGAINSTBRAINROT / DEVLOGS <span>02</span>
          </div>
          <div
            className="devlog-study"
            aria-label="A video engineering experiment: inspect, compare, normalize, combine"
          >
            <div className="devlog-inputs">
              <span>
                clip_a.mp4 <small>720p · audio</small>
              </span>
              <span>
                clip_b.mp4 <small>1080p · silence?</small>
              </span>
            </div>
            <div className="devlog-process mono">
              <span>INSPECT</span>
              <ArrowRight size={14} />
              <span>UNDERSTAND</span>
              <ArrowRight size={14} />
              <span>BUILD</span>
            </div>
            <p>
              “Combining two videos
              <br />
              <em>sounds simple.”</em>
            </p>
          </div>
          <h3>LastStandAgainstBrainRot</h3>
          <p>
            My practice in becoming a competent developer without using AI.
            Thinking through problems, writing the code, making mistakes, and
            earning the understanding myself. The devlogs follow that process.
          </p>
          {profile.devlog ? (
            <a
              className="text-link"
              href={profile.devlog}
              target="_blank"
              rel="noreferrer"
            >
              Follow the devlogs <ArrowUpRight size={16} />
            </a>
          ) : (
            <span className="channel-note">
              The process is part of the work.
            </span>
          )}
        </article>
        <aside
          className="afterimage-origin"
          aria-labelledby="afterimage-origin-title"
        >
          <span className="mono">A NAME FROM THE MOUNTAINS</span>
          <div>
            <h3 id="afterimage-origin-title">
              The details fade.
              <br />
              <em>The feeling stays.</em>
            </h3>
            <p>
              After a trek, I kept thinking about a view no camera could quite
              capture. I couldn’t remember every detail either. What stayed was
              the feeling. That is where <em>Afterimage</em> comes from: the
              impression we carry after the moment has passed.
            </p>
          </div>
        </aside>
        <div className="experiment-list">
          <span className="mono experiment-heading">EARLIER EXPLORATIONS</span>
          {archive.map((item, index) => (
            <div className="experiment" key={item.name}>
              {item.link ? (
                <a
                  className="experiment-trigger"
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="experiment-number mono">0{index + 1}</span>
                  <span>
                    <strong>{item.name}</strong>
                    <small>{item.type}</small>
                  </span>
                  <ArrowUpRight size={19} />
                </a>
              ) : (
                <>
                  <button
                    className="experiment-trigger"
                    aria-expanded={expanded === index}
                    aria-controls={`experiment-${index}`}
                    onClick={() =>
                      setExpanded(expanded === index ? null : index)
                    }
                  >
                    <span className="experiment-number mono">0{index + 1}</span>
                    <span>
                      <strong>{item.name}</strong>
                      <small>{item.type}</small>
                    </span>
                    {expanded === index ? (
                      <Minus size={18} />
                    ) : (
                      <Plus size={18} />
                    )}
                  </button>
                  <div id={`experiment-${index}`} hidden={expanded !== index}>
                    <p className="experiment-detail">{item.detail}</p>
                  </div>
                </>
              )}
            </div>
          ))}
          <p className="archive-note">
            The small things teach you how to build the bigger things.
          </p>
        </div>
      </div>
      <div className="toolkit">
        <div>
          <span className="mono">TOOLS OF THE TRADE</span>
          <h3>
            Whatever the
            <br />
            <em>idea calls for.</em>
          </h3>
        </div>
        <div className="toolkit-list">
          {toolkit.map((item) => (
            <div className="toolkit-row" key={item.title}>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
function BeyondCode() {
  return (
    <section className="beyond-section" aria-labelledby="beyond-heading">
      <div className="beyond-content">
        <SectionLabel number="04">BEYOND THE EDITOR</SectionLabel>
        <h2 id="beyond-heading">
          Good things grow
          <br />
          <em>when shared.</em>
        </h2>
        <p>Curiosity brought me to code. Community gave it somewhere to go.</p>
        <div className="community-list">
          <div>
            <span className="mono">2024—2025</span>
            <h3>KathFOSS</h3>
            <p>
              Secretary / Executive Secretary. Helping students find their feet
              with Python, Git, and GitHub workshops.
            </p>
          </div>
          <div>
            <span className="mono">2021</span>
            <h3>SEDS SXC</h3>
            <p>
              Secretary. Bringing space a little closer through CANSAT and
              high-altitude balloon projects with IoT and Python.
            </p>
          </div>
          <div>
            <span className="mono">GIVING BACK</span>
            <h3>Partnership in Education</h3>
            <p>
              Teaching and sharing technical ideas with students through the PIE
              program.
            </p>
          </div>
        </div>
        <div className="small-wins">
          <span className="mono">MOMENTS THAT MEANT SOMETHING</span>
          <p>
            Hex-Himalaya Code War 2023 <span>Absolute winner</span>
          </p>
          <p>
            NASA Space Apps 2023 <span>Global nominee</span>
          </p>
        </div>
      </div>
      <figure className="mountain-figure">
        <img
          src="/assets/IMG_2764.JPG"
          alt="Himalayan peaks emerging above clouds in Nepal"
          loading="lazy"
        />
        <figcaption>
          <span className="mono">A LITTLE PERSPECTIVE.</span>
          <span>
            Some things stay
            <br />
            <em>as a feeling.</em>
          </span>
          <span className="mono">NEPAL / HOME</span>
        </figcaption>
      </figure>
    </section>
  );
}
function Contact() {
  const [copyState, setCopyState] = useState("idle");
  const timer = useRef(null);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopyState("idle"), 3500);
  }
  return (
    <section
      className="contact-section section-space"
      id="contact"
      aria-labelledby="contact-heading"
    >
      <SectionLabel number="05">THE NEXT CONVERSATION</SectionLabel>
      <div className="contact-main">
        <div>
          <h2 id="contact-heading">
            Have a good
            <br />
            <em>what if?</em>
            <span className="contact-asterisk" aria-hidden="true">
              ✳
            </span>
          </h2>
          <p>
            An interesting problem, a wild idea, or a conversation about maths.
            <br />
            I’d love to hear it.
          </p>
        </div>
        <a
          className="contact-arrow"
          href={`mailto:${profile.email}`}
          aria-label={`Send an email to ${profile.email}`}
        >
          <ArrowUpRight strokeWidth={1} />
        </a>
      </div>
      <div className="contact-bottom">
        <div className="email-group">
          <a className="email-link" href={`mailto:${profile.email}`}>
            {profile.email}
          </a>
          <button
            className="copy-email"
            onClick={copyEmail}
            aria-label="Copy email address"
          >
            {copyState === "copied" ? <Check size={17} /> : <Copy size={17} />}
          </button>
          <span className="copy-feedback" role="status">
            {copyState === "copied"
              ? "Copied!"
              : copyState === "failed"
                ? "Please use the email link."
                : ""}
          </span>
        </div>
        <div className="social-links">
          <a href={profile.github} target="_blank" rel="noreferrer">
            GitHub <ArrowUpRight size={14} />
          </a>
          <a href={profile.linkedin} target="_blank" rel="noreferrer">
            LinkedIn <ArrowUpRight size={14} />
          </a>
          {profile.afterimage && (
            <a href={profile.afterimage} target="_blank" rel="noreferrer">
              Afterimage <ArrowUpRight size={14} />
            </a>
          )}
          {profile.devlog && (
            <a href={profile.devlog} target="_blank" rel="noreferrer">
              Devlogs <ArrowUpRight size={14} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
export default function App() {
  const [selectedProject, setSelectedProject] = useState(null);
  const projectOpenerRef = useRef(null);
  function openProject(project, opener) {
    projectOpenerRef.current = opener;
    setSelectedProject(project);
  }
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Header />
      <main id="main">
        <section
          className="hero page-width"
          id="home"
          aria-labelledby="hero-heading"
        >
          <div className="hero-copy">
            <div className="hero-eyebrow mono">
              <span className="little-asterisk" aria-hidden="true">
                ✳
              </span>{" "}
              NAMASTE, I’M PRIYAJAN
            </div>
            <h1 id="hero-heading">
              I build to
              <br />
              <em>understand.</em>
              <br />
              And to share<span className="accent">.</span>
            </h1>
            <p className="hero-description">
              AI/ML engineer. Full-stack builder. Forever a student of
              mathematics. Connecting the things I find beautiful with the
              things people find useful.
            </p>
            <div className="hero-actions">
              <a className="primary-link" href="#work">
                Explore my work <ArrowDown size={17} />
              </a>
              <a className="hero-story-link" href="#story">
                The story so far <ArrowRight size={16} />
              </a>
            </div>
          </div>
          <div className="hero-study">
            <MathStudy />
            <div className="study-caption">
              <span className="mono">FIG. 01 — CURIOSITY, IN MOTION</span>
              <span>A little maths. A lot of possibility.</span>
            </div>
          </div>
          <div className="hero-footer mono">
            <span>
              <span className="location-dot" /> BHAKTAPUR, NEPAL{" "}
              <span className="hero-timezone">/ UTC +05:45</span>
            </span>
            <span>CODE. QUESTION. UNDERSTAND. REPEAT.</span>
            <a href="#work" aria-label="Scroll to selected work">
              <ArrowDown size={15} />
            </a>
          </div>
        </section>
        <div className="intro-statement page-width">
          <span className="mono">A NOTE ON WHY</span>
          <p>
            “I continue to learn not to be best but to be capable enough to{" "}
            <em>appreciate the beauty of coding and maths</em> and to be capable
            enough to share that with people.”
          </p>
          <span className="note-attribution">— From my CV. Still true.</span>
        </div>
        <div className="page-width">
          <Work onSelect={openProject} />
        </div>
        <div className="story-wrap">
          <div className="page-width">
            <Story />
          </div>
        </div>
        <div className="page-width">
          <Notebook />
        </div>
        <BeyondCode />
        <div className="page-width">
          <Contact />
        </div>
      </main>
      <footer className="site-footer page-width">
        <a className="footer-name" href="#home">
          Priyajan<span>.</span>
        </a>
        <span className="mono">
          © {new Date().getFullYear()} · BUILT WITH INTENTION. STILL EVOLVING.
        </span>
        <a className="back-top" href="#home">
          Back to the beginning <ArrowUpRight size={15} />
        </a>
      </footer>
      {selectedProject && (
        <ProjectDialog
          project={selectedProject}
          opener={projectOpenerRef.current}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </>
  );
}
